import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminKeys } from '../api/organizations';
import { fetchInstances } from '../api/instances';

/**
 * The brands this admin may filter by and create in.
 *
 * `staleTime: Infinity`: the answer is the caller's grant intersected with the
 * deployment's configured secrets, and neither changes inside a session.
 *
 * This is the ONLY source for the brand controls. Deriving them from the
 * `clerkInstance` values present in the organizations response would be wrong in
 * both directions: those show only brands that already HAVE organisations — hiding
 * precisely the brand being onboarded, which is when someone needs to create one —
 * and they change as you page, so the options would shift under the user.
 */
export const useAdminInstances = () => {
  const query = useQuery({
    queryKey: adminKeys.instances,
    queryFn: fetchInstances,
    staleTime: Infinity,
  });

  const instances = useMemo(() => query.data?.instances ?? [], [query.data]);

  return {
    ...query,
    instances,
    // Every brand control keys off this rather than counting inline, so "one
    // instance" means the same thing in the filter row, the table column and the
    // create dialog. With one brand the value is on every row and in no decision,
    // so showing it is noise.
    isMultiInstance: instances.length > 1,
  };
};

/** Label an instance by the brands that claim it, falling back to the domain. */
export const instanceLabel = (instance) =>
  instance?.brands?.length ? instance.brands.join(' / ') : (instance?.clerkInstance ?? '');

export default useAdminInstances;
